import React from "react";
import { Link } from "react-router";
import { ArrowRight, Loader2, Lock } from "lucide-react";
import TopicLibraryEmptyState from "../guest/TopicLibraryEmptyState";
import {
  ArticleCard,
  foregroundOnSolidAccent,
} from "../../pages/guest_pages/subcategory_pages/shared/categoryDetailShared";

function LockedArticlesPanel({
  color,
  ctaTo,
  isAuthenticated,
  topicLabel,
  onRequestInterest,
  onOpenSignup,
}) {
  return (
    <div className="flex min-h-[24rem] flex-col items-center justify-center rounded-[2rem] border border-dashed border-black/20 bg-white px-8 py-16 text-center">
      <div
        className="mb-5 flex h-16 w-16 items-center justify-center rounded-2xl"
        style={{ background: `${color}18`, color }}
      >
        <Lock size={28} strokeWidth={2} />
      </div>
      <h3 className="text-xl font-black text-gray-900">
        {isAuthenticated ? "Add this topic to read articles" : "Sign in to read articles & guides"}
      </h3>
      <p className="mt-2 max-w-md text-sm leading-relaxed text-gray-500">
        {isAuthenticated ? (
          <>
            Articles and guides for{" "}
            <span className="font-semibold text-gray-700">{topicLabel}</span> unlock when this
            topic is part of your interests.
          </>
        ) : (
          <>
            Register or sign in and add{" "}
            <span className="font-semibold text-gray-700">{topicLabel}</span> to your interests to
            read provider guides in full.
          </>
        )}
      </p>
      {isAuthenticated ? (
        onRequestInterest ? (
          <button
            type="button"
            onClick={onRequestInterest}
            className="mt-7 inline-flex items-center gap-2 rounded-2xl px-6 py-3.5 text-sm font-black"
            style={{ background: color, color: foregroundOnSolidAccent(color) }}
          >
            Add topic to my interests
            <ArrowRight size={14} />
          </button>
        ) : null
      ) : onOpenSignup ? (
        <button
          type="button"
          onClick={onOpenSignup}
          className="mt-7 inline-flex items-center gap-2 rounded-2xl px-6 py-3.5 text-sm font-black"
          style={{ background: color, color: foregroundOnSolidAccent(color) }}
        >
          Register &amp; get access
          <ArrowRight size={14} />
        </button>
      ) : (
        <Link
          to={ctaTo}
          className="mt-7 inline-flex items-center gap-2 rounded-2xl px-6 py-3.5 text-sm font-black"
          style={{ background: color, color: foregroundOnSolidAccent(color) }}
        >
          Register &amp; get access
          <ArrowRight size={14} />
        </Link>
      )}
    </div>
  );
}

export default function TopicArticlesTab({
  color,
  industryId,
  slug,
  topicLabel,
  articlesState,
  canReadFull = false,
  isAuthenticated = false,
  ctaTo = "/signup",
  onRequestInterest,
  onOpenSignup,
}) {
  const { articles, loading, error, topicIds } = articlesState;
  const subCategoryLabel = topicIds?.subCategoryName || topicLabel;

  if (!canReadFull) {
    return (
      <LockedArticlesPanel
        color={color}
        ctaTo={ctaTo}
        isAuthenticated={isAuthenticated}
        topicLabel={subCategoryLabel}
        onRequestInterest={onRequestInterest}
        onOpenSignup={onOpenSignup}
      />
    );
  }

  if (loading) {
    return (
      <div
        className="flex min-h-[20rem] flex-col items-center justify-center gap-3 rounded-[2rem] border border-black/[0.06] bg-white px-8 py-16 text-center"
        role="status"
        aria-live="polite"
        aria-busy="true"
      >
        <Loader2 className="h-9 w-9 animate-spin" style={{ color }} aria-hidden />
        <p className="text-sm font-semibold text-gray-600">Loading articles &amp; guides…</p>
      </div>
    );
  }

  if (error) {
    return (
      <TopicLibraryEmptyState kind="articles" topicLabel={subCategoryLabel} color={color} />
    );
  }

  if (!articles.length) {
    return (
      <TopicLibraryEmptyState kind="articles" topicLabel={subCategoryLabel} color={color} />
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-[10px] font-black uppercase tracking-[0.28em] text-gray-400">Library</p>
          <h2 className="mt-1 text-2xl font-black text-slate-950 sm:text-3xl">Articles &amp; guides</h2>
          <p className="mt-2 text-sm text-gray-500">
            Provider guides for{" "}
            <span className="font-semibold text-gray-700">{subCategoryLabel}</span>. You have full
            access to every guide in this topic.
          </p>
        </div>
        <span
          className="shrink-0 rounded-lg px-3 py-1 text-xs font-black"
          style={{ background: `${color}18`, color }}
        >
          {articles.length} available
        </span>
      </div>

      <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
        {articles.map((article) => (
          <ArticleCard
            key={article.slug}
            article={{
              ...article,
              tags: article.providerName ? [article.providerName] : [],
            }}
            to={`/${industryId}/${slug}/${article.slug}`}
            color={color}
            gated={false}
          />
        ))}
      </div>

      <div
        className="overflow-hidden rounded-[2rem] border border-black/[0.08] bg-white p-6 sm:p-8"
        style={{ borderColor: `${color}35` }}
      >
        <p className="text-sm leading-relaxed text-gray-600">
          Each guide is authored by a verified provider in this subcategory. Browse related guides
          and connect with your provider when you are ready.
        </p>
        <Link
          to={`/${industryId}/${slug}`}
          className="mt-4 inline-flex items-center gap-2 text-sm font-black"
          style={{ color }}
        >
          Back to topic overview
          <ArrowRight size={14} />
        </Link>
      </div>
    </div>
  );
}
